<template>
  <div
    :style="'height: 100%; background: ' + backgroundColor"
    class="table-item-card"
  >
    <v-divider v-if="flat" />
    <v-card :elevation="flat ? 0 : 1" class="item-card">
      <div class="d-flex align-center justify-start top-bar">
        <div class="md-light" v-if="title">{{ title }}</div>
        <v-spacer />
        <slot name="actions" />
      </div>

      <div class="d-flex align-start" style="width: 100%">
        <div class="item-info">
          <v-row class="info-row" no-gutters>
            <v-col
              v-for="(header, index) in formattedHeaders"
              :key="header + '_' + index"
              class="px-1"
            >
              <div class="d-flex justify-start align-center" v-if="header">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      size="small"
                      variant="flat"
                      icon
                      @click="sortColumn(header.value)"
                      v-bind="props"
                      class="flex-shrink-0"
                    >
                      <v-icon
                        v-if="header && header.value === pagination.sortBy"
                        size="small"
                        icon="arrow_upward"
                        :style="pagination.descending ? 'rotate: 180deg' : ''"
                      />
                      <v-icon
                        v-else
                        size="small"
                        icon="search"
                      />
                    </v-btn>
                  </template>
                  <span>Sort by {{ header.text }}</span>
                </v-tooltip>
                <div v-if="hideSearch">
                  <div class="md-light">Sort by</div>
                  <div class="md-body-no-spacing">
                    {{ header.text }}
                  </div>
                </div>
                <v-text-field
                  v-else
                  :label="header.text"
                  v-model="filters[header.value]"
                  @keyup="filterUpdated"
                  density="compact"
                  hide-details
                  class="flex-grow-1"
                />
              </div>
            </v-col>
          </v-row>
        </div>

        <div style="width: 60px" v-if="mobile">
          <v-btn icon variant="plain" @click="drawer = !drawer" class="mt-4">
            <v-icon v-if="!drawer" icon="expand_more" />
            <v-icon v-else icon="expand_less" />
          </v-btn>
        </div>
      </div>
    </v-card>
    <v-divider v-if="flat" />
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import store from "@/store";

import helpers from "@/mixins/helpers";

export default {
  components: {},
  mixins: [helpers],

  props: {
    headers: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    flat: {
      type: Boolean,
      required: false,
    },
    pagination: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: false,
    },
    backgroundColor: {
      type: String,
      required: false,
      default: "#FFFFFF",
    },
    hideSearch: {
      type: Boolean,
      required: false,
    },
  },

  data() {
    return {
      /*
            This object will be updated by the filters above
            Whenever a filter above gets modified, we format the filter, and then send that info to the containing component
        */
      filters: {},
      drawer: false,
      mobile: window.innerWidth < 960,
    };
  },

  computed: {
    formattedHeaders() {
      if (!this.mobile || this.drawer || !this.headers) {
        return this.headers;
      } else {
        return [this.headers[0]];
      }
    },
  },

  watch: {},

  mounted() {},
  created() {
    window.addEventListener("resize", () => {
      let width = window.innerWidth;
      if (width < 960) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }
    });
  },

  unmounted() {
    window.removeEventListener("resize", () => {
      let width = window.innerWidth;
      if (width < 960) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }
    });
  },
  methods: {
    click() {
      this.$emit("click");
    },

    // handles sorting selected column by ascending / descending
    // and handles selecting different columns to sort
    // directly modifies the pagination prop given by the containing class
    sortColumn(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },

    formatFilters() {
      let filterValues = {};
      for (const key in this.filters) {
        if (Object.hasOwnProperty.call(this.filters, key)) {
          const filterValue = this.filters[key];
          let newKey = key.substring(key.indexOf(".") + 1, key.length);
          filterValues[newKey] = this.filters[key];
        }
      }
      return filterValues;
    },

    filterUpdated() {
      this.itemsPending = true;
      let formattedFilters;
      let orderBy = "";
      let limit;
      try {
        const { sortBy, descending, page, rowsPerPage } = this.pagination;
        if (sortBy) {
          let formattedSortBy = sortBy.substring(sortBy.indexOf(".") + 1);
          if (formattedSortBy != "id") {
            formattedSortBy = formattedSortBy.replace(" ", "_").toLowerCase();
            formattedSortBy = "cast(_" + formattedSortBy + " as text)";
          }
          if (descending) {
            orderBy = "order by " + formattedSortBy + " desc";
          } else {
            orderBy = "order by " + formattedSortBy;
          }
        } else {
          orderBy = "order by id";
        }
        limit =
          (rowsPerPage < 0 ? "" : " limit " + rowsPerPage) +
          " offset " +
          (page - 1) * rowsPerPage;
        formattedFilters = this.formatFilters();
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
      } finally {
        let filterInfo = {
          formattedFilters: formattedFilters,
          limit: limit,
          orderBy: orderBy,
        };
        this.$emit("filterUpdated", filterInfo);
      }
    },
  },
};
</script>

<style scoped>
.top-bar {
  margin-right: 14px;
  padding-left: 16px;
  padding-top: 8px;
}

.item-info {
  width: 100%;
}

.item-card {
  width: 100%;
  border-radius: 5px;
  padding: 8px;
}

.show-arrow-inactive {
  color: gray;
  opacity: 1;
}

.search-button {
  padding: 0px !important;
}

.search-button-icon {
  padding-top: 12px;
}

.table-item-card {
  padding-bottom: 4px;
}
</style>

